import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { GuestAccountCard } from "../components/GuestAccountCard";

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [order, setOrder] = useState(null);
  const tries = useRef(0);
  const { clear } = useCart();
  const { user, loading } = useAuth();
  const [wasGuest, setWasGuest] = useState(null);

  useEffect(() => {
    if (!loading && wasGuest === null) setWasGuest(!user);
  }, [loading, user, wasGuest]);

  useEffect(() => {
    if (!sessionId) return;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus(data.payment_status);
        if (data.order_id) setOrder(data.order_id);
        if (data.payment_status === "paid") { clear(); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") return;
      } catch {}
      if (tries.current < 30) setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, clear]);

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center">
      {status === "paid" ? (
        <>
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" data-testid="payment-success-icon" />
          <h1 className="font-display text-3xl font-extrabold mt-4">Payment successful!</h1>
          <p className="text-slate-600 mt-2">Your order has been sent to our wholesaler for auto-fulfillment.{!user && " We've emailed your receipt — bookmark the tracking page to follow your order."}</p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to={user ? "/orders" : `/track/${order}`}><Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="payment-view-orders-btn">Track fulfillment →</Button></Link>
            <Link to="/shop"><Button variant="outline" className="rounded-full">Keep shopping</Button></Link>
          </div>
          {wasGuest && order && <GuestAccountCard orderId={order} />}
        </>
      ) : (
        <>
          <Loader2 className="w-16 h-16 text-indigo-500 mx-auto animate-spin" />
          <h1 className="font-display text-2xl font-extrabold mt-4">Confirming payment…</h1>
          <p className="text-slate-600 mt-2">Hold tight — this usually takes a couple of seconds.</p>
        </>
      )}
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="max-w-lg mx-auto px-6 py-24 text-center">
    <XCircle className="w-16 h-16 text-slate-400 mx-auto" />
    <h1 className="font-display text-3xl font-extrabold mt-4">Payment cancelled</h1>
    <p className="text-slate-600 mt-2">Your cart is safe. You can pick up where you left off any time.</p>
    <Link to="/cart"><Button className="mt-6 rounded-full bg-indigo-600 hover:bg-indigo-700">Back to cart</Button></Link>
  </div>
);
